import React, { useEffect, lazy, Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import { LanguageProvider } from './contexts/LanguageContext';
import { trackPageView } from './utils/analytics';
import MetaTags from './components/SEO/MetaTags';
import StructuredData from './components/SEO/StructuredData';
import PerformanceMonitor from './components/PerformanceMonitor';
import './App.css';

// Lazy load components for better performance
const LandingPage = lazy(() => import('./components/LandingPage'));
const Support = lazy(() => import('./components/Support'));
const Contact = lazy(() => import('./components/Contact'));
const FAQ = lazy(() => import('./components/FAQ'));
const PrivacyPolicy = lazy(() => import('./components/PrivacyPolicy'));
const TermsOfService = lazy(() => import('./components/TermsOfService'));
const Imprint = lazy(() => import('./components/Imprint'));
const EULA = lazy(() => import('./components/EULA'));

// Loading component for Suspense fallback
const LoadingFallback: React.FC = () => (
  <div style={{
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    height: '100vh',
    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
  }}>
    <div className="loader" style={{
      border: '4px solid rgba(255, 255, 255, 0.3)',
      borderTop: '4px solid white',
      borderRadius: '50%',
      width: '50px',
      height: '50px',
      animation: 'spin 1s linear infinite'
    }} />
    <style>{`
      @keyframes spin {
        0% { transform: rotate(0deg); }
        100% { transform: rotate(360deg); }
      }
    `}</style>
  </div>
);

// Analytics wrapper component to track page views
function AnalyticsTracker() {
  const location = useLocation();

  useEffect(() => {
    // Track page view on route change
    trackPageView(location.pathname);
  }, [location]);

  return null;
}

function App() {
  return (
    <HelmetProvider>
      <LanguageProvider>
        <Router>
          <AnalyticsTracker />
          <MetaTags />
          <StructuredData />
          <PerformanceMonitor />
          <div className="App">
            <Suspense fallback={<LoadingFallback />}>
              <Routes>
                <Route path="/" element={<LandingPage />} />
                <Route path="/en" element={<LandingPage />} />
                <Route path="/es" element={<LandingPage />} />
                <Route path="/fr" element={<LandingPage />} />

                {/* Support & Contact Pages */}
                <Route path="/support" element={<Support />} />
                <Route path="/contact" element={<Contact />} />
                <Route path="/faq" element={<FAQ />} />

                {/* Legal Pages - React components for SPA navigation */}
                <Route path="/privacy" element={<PrivacyPolicy />} />
                <Route path="/terms" element={<TermsOfService />} />
                <Route path="/imprint" element={<Imprint />} />
                <Route path="/eula" element={<EULA />} />

                {/* Admin */}
                <Route path="/admin/*" element={<AdminRedirect />} />

                {/* 404 Fallback */}
                <Route path="*" element={<Navigate to="/" replace />} />
              </Routes>
            </Suspense>
          </div>
        </Router>
      </LanguageProvider>
    </HelmetProvider>
  );
}

// Redirect to admin interface (will be served separately)
const AdminRedirect: React.FC = () => {
  React.useEffect(() => {
    // In production, this would redirect to the actual admin subdomain or path
    window.location.href = 'http://localhost:3001'; // Admin dev server
  }, []);

  return (
    <div style={{ padding: '2rem', textAlign: 'center' }}>
      <h2>🔄 Redirecting to Admin Panel...</h2>
      <p>You are being redirected to the Admin Dashboard.</p>
      <a href="http://localhost:3001" style={{ color: '#FB923C' }}>
        Click here if the redirect doesn't work
      </a>
    </div>
  );
};

export default App; 